'use client';
import React from 'react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import * as rModal from '@radix-ui/react-dialog';
import cn from 'classnames';
import styles from './modal.module.sass';
import { FontIcon, Spinner, Text } from '@intigral/shared';
import { useVisualViewportRect } from '@intigral/hooks/dom/useVisualViewportRect/useVisualViewportRect';
import withPresetWidth, { widthPresets } from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import withResponsiveStyles, {
    ResponsiveValue,
} from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';

export interface ModalProps {
    children: React.ReactNode;
    open?: boolean;
    onOpenChange: (open?: boolean) => void;
    closePlacement?: 'start' | 'end' | 'null' | null;
    closeSize?: 'small';
    theme?: 'black';
    widthSet?: keyof typeof widthPresets;
    back?: boolean;
    alignSelf?: 'bottom';
    className?: string;
    padding?: ResponsiveValue;
    title?: string;
    isLoading?: boolean;
    /** Optional. When set (e.g. fullscreen element), portal renders into it instead of body. Used for No Ads CTA in fullscreen. */
    container?: HTMLElement;
    /** Keep the overlay inside the visible area while the on-screen keyboard covers part of it (iOS Safari). */
    fitVisualViewport?: boolean;
}

const OverlayStyle = withResponsiveStyles(rModal.Content);
const OverlayWidth = withPresetWidth(OverlayStyle);

export const Modal = ({
    children,
    open,
    onOpenChange,
    closePlacement = 'end',
    closeSize,
    theme,
    widthSet,
    back,
    alignSelf = 'bottom',
    padding,
    title,
    className,
    isLoading,
    container,
    fitVisualViewport = false,
}: ModalProps) => {
    const hideClose = closePlacement === null || closePlacement === 'null';
    const visibleRect = useVisualViewportRect(fitVisualViewport && !!open);

    return (
        <rModal.Root open={open} onOpenChange={onOpenChange}>
            <rModal.Portal {...(container && { container })}>
                <rModal.Overlay
                    className={cn(styles.overlay, { [styles[`align-${alignSelf}`]]: alignSelf })}
                    style={
                        visibleRect ? { top: visibleRect.top, height: visibleRect.height, bottom: 'auto' } : undefined
                    }
                    onPointerDown={(event) => {
                        // Backdrop only: prevent the ghost-click that would hit whatever is under
                        // the dialog (e.g. Continue Watching play) after the overlay unmounts.
                        if (event.target === event.currentTarget) {
                            event.preventDefault();
                            onOpenChange(false);
                        }
                    }}
                >
                    <OverlayWidth
                        className={cn(styles.content, { [styles[`theme-${theme}`]]: theme }, className)}
                        presetWidth={widthSet}
                        padding={padding}
                        aria-describedby={undefined}
                        onPointerDownOutside={(event) => {
                            // Radix dismisses on outside pointerdown; stopping the event keeps the
                            // follow-up click from landing on the card behind the drawer.
                            // Clicks inside portalled popovers (Select, etc.) are excluded.
                            const target = event.target as HTMLElement | null;
                            if (target?.closest?.('[data-radix-popper-content-wrapper], [role="listbox"]')) {
                                return;
                            }
                            event.preventDefault();
                            onOpenChange(false);
                        }}
                    >
                        {/* https://www.radix-ui.com/primitives/docs/components/dialog#title */}
                        <VisuallyHidden>
                            <rModal.Title className="DialogTitle">{title ?? 'Modal'}</rModal.Title>
                        </VisuallyHidden>

                        {isLoading ? (
                            <Spinner block style={{ minHeight: '80vh' }} />
                        ) : (
                            <>
                                {!hideClose && (
                                    <rModal.Close
                                        className={cn(
                                            styles.close,
                                            styles[`close-${closePlacement}`],
                                            closeSize && styles[`${closeSize}`]
                                        )}
                                        data-test-id="modal-close-btn"
                                    >
                                        {back ? (
                                            <FontIcon name="arrow-left" size="smaller" md="small" />
                                        ) : (
                                            <FontIcon name="close" size="smaller" md="small" />
                                        )}
                                    </rModal.Close>
                                )}

                                {title && <strong className={styles.title}>{title}</strong>}

                                {children && (
                                    <div className={cn(styles.children, hideClose && styles.noclose)}>{children}</div>
                                )}
                            </>
                        )}
                    </OverlayWidth>
                </rModal.Overlay>
            </rModal.Portal>
        </rModal.Root>
    );
};
